import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const voiceFaqs = [
  {
    id: 'v1',
    q: 'Who provides chimney cleaning near me in Jacksonville?',
    a: 'BBM Services is Jacksonville\'s top-rated chimney cleaning company, serving all neighborhoods in Duval, St. Johns, Clay, and Nassau counties. Our CSIA-certified technicians provide thorough chimney inspections and cleaning with same-week availability throughout the Jacksonville metropolitan area.',
  },
  {
    id: 'v2',
    q: 'How often should air ducts be cleaned?',
    a: 'The National Air Duct Cleaners Association (NADCA) recommends cleaning air ducts every 3–5 years. However, Jacksonville homes with pets, allergies, recent renovations, or visible mold growth may benefit from more frequent cleaning. Our technicians will inspect your system and advise accordingly.',
  },
  {
    id: 'v3',
    q: 'How much does dryer vent cleaning cost in Jacksonville?',
    a: 'Dryer vent cleaning in Jacksonville typically costs between $99–$175, depending on duct length and accessibility. BBM Services provides free, upfront quotes with no surprise charges. Regular cleaning is one of the most cost-effective ways to prevent house fires and improve dryer efficiency.',
  },
  {
    id: 'v4',
    q: 'What are signs a chimney needs cleaning?',
    a: 'Key signs include: dark smoke or unusual odors when using your fireplace, visible creosote buildup inside the flue, animals or birds nesting in the chimney, reduced draft or poor fireplace performance, and it\'s been more than a year since your last cleaning. Call BBM Services for a same-week inspection.',
  },
  {
    id: 'v5',
    q: 'Is dryer vent cleaning really necessary?',
    a: 'Absolutely — the U.S. Fire Administration reports nearly 2,900 home fires annually are caused by dryer vent clogs. Cleaning your dryer vent removes lint buildup that restricts airflow, reduces drying time by 25–30%, lowers energy bills, and most importantly prevents dangerous fires from starting in your home.',
  },
  {
    id: 'v6',
    q: 'How long does air duct cleaning take?',
    a: 'A complete air duct cleaning for an average Jacksonville home takes 3–5 hours. Larger homes or heavily soiled systems may take longer. Our NADCA-certified technicians use powerful negative-pressure equipment to clean every duct run, ensuring your system is fully cleared before they leave.',
  },
  {
    id: 'v7',
    q: 'Do I need to be home during the service?',
    a: 'Yes, we recommend a homeowner or responsible adult be present for access and to walk through our findings at the end. We always call 30 minutes before arrival, protect your floors and furniture throughout the job, and leave your home spotless after every service.',
  },
];

export default function FAQSection() {
  const [openId, setOpenId] = useState<string | null>('v1');
  const { ref, isVisible } = useScrollAnimation();

  const leftFaqs = voiceFaqs.slice(0, 4);
  const rightFaqs = voiceFaqs.slice(4);

  return (
    <section className="py-28 bg-stone-50" id="faq">
      {/* FAQPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: voiceFaqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          }),
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`flex flex-col lg:flex-row gap-12 items-start transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Left: Header + first 4 FAQs */}
          <div className="lg:w-1/2">
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-4">
                <div className="h-px w-8 bg-brand-amber" />
                Voice Search &amp; FAQ
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark mb-4 leading-tight">
                Common Questions Jacksonville Homeowners Ask
              </h2>
              <p className="text-brand-warm-gray text-base leading-relaxed">
                Quick, clear answers about chimney cleaning, air duct cleaning, and dryer vent cleaning in Jacksonville, FL.
              </p>
            </div>

            <div className="space-y-3">
              {leftFaqs.map((faq) => (
                <div
                  key={faq.id}
                  className={`border-2 rounded-2xl overflow-hidden transition-all duration-200 ${
                    openId === faq.id ? 'border-brand-amber bg-white' : 'border-stone-200 bg-white hover:border-stone-300'
                  }`}
                >
                  <button
                    className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                    onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                  >
                    <span className={`font-semibold text-sm leading-snug pt-0.5 ${openId === faq.id ? 'text-brand-amber' : 'text-brand-dark'}`}>
                      {faq.q}
                    </span>
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${openId === faq.id ? 'bg-brand-amber text-white' : 'bg-stone-100 text-brand-warm-gray'}`}>
                      <i className={`${openId === faq.id ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                    </div>
                  </button>
                  {openId === faq.id && (
                    <div className="px-6 pb-5">
                      <p className="text-brand-warm-gray text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Remaining FAQs + CTA */}
          <div className="lg:w-1/2 lg:pt-[178px]">
            <div className="space-y-3 mb-8">
              {rightFaqs.map((faq) => (
                <div
                  key={faq.id}
                  className={`border-2 rounded-2xl overflow-hidden transition-all duration-200 ${
                    openId === faq.id ? 'border-brand-amber bg-white' : 'border-stone-200 bg-white hover:border-stone-300'
                  }`}
                >
                  <button
                    className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                    onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                  >
                    <span className={`font-semibold text-sm leading-snug pt-0.5 ${openId === faq.id ? 'text-brand-amber' : 'text-brand-dark'}`}>
                      {faq.q}
                    </span>
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${openId === faq.id ? 'bg-brand-amber text-white' : 'bg-stone-100 text-brand-warm-gray'}`}>
                      <i className={`${openId === faq.id ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                    </div>
                  </button>
                  {openId === faq.id && (
                    <div className="px-6 pb-5">
                      <p className="text-brand-warm-gray text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* CTA block */}
            <div className="bg-brand-dark rounded-2xl p-7">
              <h3 className="font-serif text-xl font-bold text-white mb-2">Still have questions?</h3>
              <p className="text-white/55 text-sm mb-5">Our team is happy to answer any questions about your specific home or situation — no obligation.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/faq"
                  className="flex-1 bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-3 px-5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap text-center"
                >
                  Full FAQ Page
                </Link>
                <a
                  href="tel:+19045550191"
                  className="flex-1 border-2 border-white/20 hover:border-white/40 text-white font-semibold py-3 px-5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap text-center"
                >
                  Call Us Directly
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
